import { selectInputWhenFocussed } from '@/utils/selectInputWhenFocussed';
import clsx from 'clsx';
import {
  ChangeEventHandler,
  InputHTMLAttributes,
  PropsWithChildren,
  forwardRef,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { CheckboxIcon } from 'ui/icons';

type IProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'title'> & {
  icon?: (() => JSX.Element) | null;
  title?: React.ReactNode;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  selected?: boolean;
  color?: string;
  containerClassName?: string;
  titleClassName?: string;
  iconClassName?: string;
  checkBoxColor?: string;
};

const Checkbox = forwardRef<HTMLInputElement, PropsWithChildren<IProps>>(
  (props, ref) => {
    const {
      title,
      icon: Icon = CheckboxIcon,
      selected,
      children,
      className,
      color,
      containerClassName,
      titleClassName,
      iconClassName,
      checkBoxColor,
      ...rest
    } = props;
    return (
      <label
        htmlFor={rest.id}
        tabIndex={0}
        onKeyUp={selectInputWhenFocussed(rest.id ?? '')}
        className={twMerge(
          className,
          clsx({
            'flex flex-col items-stretch w-full gap-4 transition-colors select-none':
              true,
            '!border-gray-500': selected || rest.checked,
            'cursor-not-allowed': rest.disabled,
            'cursor-pointer': !rest.disabled,
          }),
        )}
      >
        <div
          className={clsx({
            'relative flex items-center w-full gap-4 transition-colors': true,
            '!border-gray-500': selected || rest.checked,
            'cursor-not-allowed': rest.disabled,
            'cursor-pointer': !rest.disabled,
          })}
        >
          <div className="flex absolute ">
            <input
              ref={ref}
              id={rest.id}
              name={rest.name}
              className="w-[1px] h-[1px] invisible"
              type="checkbox"
              checked={selected || rest.checked}
              {...rest}
            />
          </div>
          <div
            className={twMerge(
              containerClassName,
              'w-full py-4 gap-2 md:gap-4 items-center flex',
            )}
          >
            <div className={twMerge(iconClassName, 'flex-shrink-0')}>
              {Icon ? (
                <Icon
                  height={16}
                  width={16}
                  color={color}
                  isChecked={selected}
                  checkBoxColor={checkBoxColor}
                />
              ) : null}
            </div>
            <div>
              <p className={clsx('text-gray-500 text-sm', titleClassName)}>
                {title}
              </p>
            </div>
          </div>
        </div>
        {!selected || rest.checked ? children : null}
      </label>
    );
  },
);

export default Checkbox;
